import { useEffect, useRef, useState } from 'react';

export default function MilestoneCard({ milestone, index, totalCards }) {
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const el = cardRef.current;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setIsVisible(true);
        if (el) observer.unobserve(el);
      }
    }, { threshold: 0.1 });
    if (el) observer.observe(el);
    return () => {
      if (el) observer.unobserve(el);
    };
  }, []);
  
  const zIndex = totalCards + index;
  const rotation = index % 2 === 0 ? -1 : 1;
  const isEven = index % 2 === 0;
  
  return (
    <div
      ref={cardRef}
      className={`sticky top-[10vh] mb-10 md:mb-16 transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ zIndex, transitionDelay: `${index * 150}ms` }}
    >
      {/* Mobile: Full width single column layout */}
      <div className="md:hidden relative">
        {/* Timeline dot - positioned on left edge */}
        <div
          className={`absolute -left-3 top-6 w-4 h-4 rounded-full bg-timeline border-3 border-timeline z-10 transition-transform duration-500 ${
            isVisible ? 'scale-100' : 'scale-0'
          }`}
          style={{ transitionDelay: `${index * 150 + 100}ms` }}
        />
        {/* Card - full width */}
        <div
          className="bg-card-bg rounded-lg shadow-sm border milestone-card p-4 ml-4 relative"
          style={{ transform: `rotate(${rotation * 0.5}deg)` }}
        >
          <div className="flex justify-between items-start mb-2">
            <div className="text-sm font-bold text-year">{milestone.year}</div>
            <div className="text-2xl font-bold text-secondary-text/30">
              {(index + 1).toString().padStart(2, '0')}
            </div>
          </div>
          <h3 className="text-base font-semibold mb-2 text-primary-text">{milestone.title}</h3>
          <p className="text-secondary-text text-sm leading-relaxed">{milestone.description}</p>
        </div>
      </div>

      {/* Desktop: Alternating left/right layout */}
      <div className="hidden md:flex relative flex-row gap-8">
        {/* Timeline dot */}
        <div
          className={`absolute left-1/2 -translate-x-1/2 top-6 w-6 h-6 rounded-full bg-timeline border-4 border-timeline z-10 transition-transform duration-500 ${
            isVisible ? 'scale-100' : 'scale-0'
          }`}
          style={{ transitionDelay: `${index * 150 + 100}ms` }}
        />
        {/* Left column (shows for odd indices) */}
        <div className="w-1/2 relative">
          {!isEven && (
            <div
              className="bg-card-bg rounded-lg shadow-sm border milestone-card p-6 mr-8 relative"
              style={{ transform: `rotate(${rotation}deg)` }}
            >
              <div className="text-sm font-bold text-year mb-1">{milestone.year}</div>
              <h3 className="text-xl font-semibold mb-2 text-primary-text">{milestone.title}</h3>
              <p className="text-secondary-text">{milestone.description}</p>
              <div className="absolute top-4 right-4 text-4xl font-bold text-secondary-text/30">
                {(index + 1).toString().padStart(2, '0')}
              </div>
            </div>
          )}
        </div>
        {/* Right column (shows for even indices) */}
        <div className="w-1/2 relative">
          {isEven && (
            <div
              className="bg-card-bg rounded-lg shadow-sm border milestone-card p-6 ml-8 relative"
              style={{ transform: `rotate(${rotation}deg)` }}
            >
              <div className="text-sm font-bold text-year mb-1">{milestone.year}</div>
              <h3 className="text-xl font-semibold mb-2 text-primary-text">{milestone.title}</h3>
              <p className="text-secondary-text">{milestone.description}</p>
              <div className="absolute top-4 right-4 text-4xl font-bold text-secondary-text/30">
                {(index + 1).toString().padStart(2, '0')}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
